import type { Asset, AssetBlob } from "@roqeet/core";
import { getDB } from "../platform/db/index.js";
import { validateBackupImportDataForRestore } from "../platform/db/storage-provider.js";
import { storage } from "./storage.js";

const MIB = 1024 * 1024;
export const BACKUP_JSON_SIZE_LIMIT_BYTES = 20 * MIB;

interface BackupFile {
  format: "roqeet-backup";
  version: 1;
  exportedAt: number;
  data: unknown;
  blobs: Record<string, { mime: string; base64: string }>;
}

type BackupBlobRecord = BackupFile["blobs"];

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(base64: string, mime: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function formatBackupSize(bytes: number): string {
  return `${Math.round(bytes / MIB)} MB`;
}

export async function buildBackup(): Promise<string> {
  const data = await storage.exportAll();

  const assets = await storage.assets.list();
  const blobs: BackupBlobRecord = {};
  for (const asset of assets as Asset[]) {
    if (blobs[asset.hash]) continue;
    const blob = await storage.getAssetBlob(asset.hash);
    if (blob) {
      blobs[asset.hash] = { mime: asset.mime, base64: await blobToBase64(blob.data) };
    }
  }

  const file: BackupFile = {
    format: "roqeet-backup",
    version: 1,
    exportedAt: Date.now(),
    data,
    blobs,
  };
  return JSON.stringify(file);
}

export async function downloadBackup(): Promise<void> {
  const json = await buildBackup();
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  a.href = url;
  a.download = `roqeet-backup-${ts}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function restoreBackup(file: Blob): Promise<void> {
  if (file.size > BACKUP_JSON_SIZE_LIMIT_BYTES) {
    throw new Error(
      `Backup file is too large. Maximum size is ${formatBackupSize(BACKUP_JSON_SIZE_LIMIT_BYTES)}.`,
    );
  }

  const text = await file.text();
  const parsed = parseBackupFile(text);
  const stagedBlobs = stageBackupBlobs(parsed.blobs);
  if (parsed.data) {
    validateBackupImportDataForRestore(parsed.data);
  }

  const writtenBlobHashes = await restoreStagedBlobs(stagedBlobs);

  try {
    if (parsed.data) {
      await storage.importAll(parsed.data);
    }
  } catch (error) {
    await deleteWrittenAssetBlobs(writtenBlobHashes);
    throw error;
  }
}

async function restoreStagedBlobs(stagedBlobs: AssetBlob[]): Promise<string[]> {
  const writtenBlobHashes: string[] = [];
  try {
    for (const blob of stagedBlobs) {
      const existing = await storage.getAssetBlob(blob.hash);
      if (!existing) {
        await storage.putAssetBlob(blob);
        writtenBlobHashes.push(blob.hash);
      }
    }
  } catch (error) {
    await deleteWrittenAssetBlobs(writtenBlobHashes);
    throw error;
  }
  return writtenBlobHashes;
}

async function deleteWrittenAssetBlobs(hashes: string[]): Promise<void> {
  await Promise.allSettled(hashes.map((hash) => getDB().assetBlobs.delete(hash)));
}

function parseBackupFile(text: string): Partial<BackupFile> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("Backup JSON could not be parsed.");
  }

  if (!isRecord(parsed)) {
    throw new Error("Backup file root must be an object.");
  }
  if (parsed["format"] !== "roqeet-backup") {
    throw new Error("This is not a valid RoQeet Launcher backup file.");
  }
  if (parsed["version"] !== undefined && parsed["version"] !== 1) {
    throw new Error("Unsupported RoQeet backup version.");
  }
  if (parsed["data"] !== undefined && !isRecord(parsed["data"])) {
    throw new Error("Backup data must be an object.");
  }
  if (parsed["blobs"] !== undefined && !isRecord(parsed["blobs"])) {
    throw new Error("Backup blobs must be an object.");
  }

  return parsed as Partial<BackupFile>;
}

function stageBackupBlobs(blobs: BackupBlobRecord | undefined): AssetBlob[] {
  if (!blobs) return [];
  const staged: AssetBlob[] = [];
  for (const [hash, entry] of Object.entries(blobs)) {
    if (!hash.trim() || hash.includes("\0")) {
      throw new Error("Backup blob hash is invalid.");
    }
    if (!isRecord(entry) || typeof entry["mime"] !== "string" || typeof entry["base64"] !== "string") {
      throw new Error(`Backup blob "${hash}" is malformed.`);
    }
    try {
      staged.push({ hash, data: base64ToBlob(entry["base64"], entry["mime"]) });
    } catch {
      throw new Error(`Backup blob "${hash}" is not valid base64.`);
    }
  }
  return staged;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}
